import { useReducer, useContext } from 'react'
import {
  Box,
  Typography,
  Tooltip,
  Modal,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from 'redux/Hook'
import exportUsersApi from 'api/Participant/ExportParticipants'
import { ParticpantsContext } from 'containers/Participants'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
}

interface State {
  isOpen: boolean
  isFetching: boolean
  email: string
  fileName: string
  message: string
}

const initialState: State = {
  isOpen: false,
  isFetching: false,
  email: '',
  fileName: '',
  message: '',
}

const ExportParticipants = () => {
  const { state: participantData } = useContext(ParticpantsContext)

  const [state, dispatch] = useReducer(
    (state: State, action: { type: any; payload: any }) => {
      switch (action.type) {
        case 'TOGGLE_MODAL_WITH_DATA':
          return { ...state, ...action.payload }
        case 'SET_FETCHING':
          return { ...state, isFetching: action.payload }
        default:
          return state
      }
    },
    initialState,
  )

  const { t } = useTranslation()

  const segmentId = useAppSelector((state) => state.filterBuilder.segmentId)
  const features = useAppSelector((state) => state.features)
  const isPRMEXPORTEnabled = features.features['prm_export']?.enabled || false

  const onClose = () => {
    dispatch({
      type: 'TOGGLE_MODAL_WITH_DATA',
      payload: {
        isOpen: false,
        isFetching: false,
        email: '',
        fileName: '',
        message: '',
      },
    })
  }

  const handleClick = async () => {
    try {
      dispatch({ type: 'SET_FETCHING', payload: true })
      const { meta } = participantData

      const participantSegmentId = segmentId || '0'
      const searchText = participantData.searchInputRef.current?.value || ''

      const requestData: any = {
        participant_count: meta.count || 0,
      }

      if (searchText) {
        requestData.filters = {
          search: searchText,
        }
      }

      if (participantSegmentId !== '0') {
        requestData.participant_segment_id = participantSegmentId
      }

      const response = await exportUsersApi.exportUsers(requestData)

      if (response.status === 200) {
        const { status, email, file, message } = response.data

        const shouldShowModal = [
          'queued',
          'already_queued',
          'too_many_participants',
        ].includes(status)

        if (shouldShowModal) {
          dispatch({
            type: 'TOGGLE_MODAL_WITH_DATA',
            payload: {
              isOpen: true,
              isFetching: false,
              email: email || '',
              fileName: file || '',
              message,
            },
          })
        }
      }

      dispatch({ type: 'SET_FETCHING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_FETCHING', payload: false })
    }
  }

  const renderButton = () => {
    if (!isPRMEXPORTEnabled) return null

    const { meta } = participantData
    const hasEnoughParticipants = meta.count > 0

    if (hasEnoughParticipants) {
      return (
        <Button
          onClick={handleClick}
          data-testid="export-participants-button"
          disabled={state.isFetching}
          size="large"
          startIcon={
            state.isFetching ? (
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={20} />
              </Box>
            ) : (
              <DownloadIcon />
            )
          }
          variant="outlined"
        >
          {t('participant.export')}
        </Button>
      )
    }

    return (
      <Tooltip title={t('participant.noParticipantsExport')} arrow>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 1 }}>
          <Button
            data-testid="export-participants-button"
            disabled
            startIcon={<DownloadIcon />}
            variant="outlined"
            size="large"
          >
            {t('participant.export')}
          </Button>
        </Box>
      </Tooltip>
    )
  }

  return (
    <>
      {renderButton()}
      {state.isOpen && (
        <Modal open={true} onClose={onClose}>
          <Box sx={style} data-testid="export-participants-modal">
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <InfoOutlinedIcon color="primary" />
              <Typography variant="h6" component="h2">
                {t('common.downloadViaEmail')}
              </Typography>
            </Stack>

            <Typography sx={{ mb: 2 }}>{state.message}</Typography>

            {state.fileName && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t('common.file')}:</strong> {state.fileName}
              </Typography>
            )}

            {state.email && (
              <Typography variant="body2" sx={{ mb: 3 }}>
                <strong> {t('common.emailAddress')}:</strong> {state.email}
              </Typography>
            )}

            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={onClose}>
                {t('common.ok')}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  )
}

export default ExportParticipants
